import { ApiError } from '../../lib/api';

export interface QueryErrorProps {
  error: unknown;
  onRetry?: () => void;
  /** Compact mode hides the description and shows just the title + retry. */
  compact?: boolean;
}

/**
 * Inline error banner for failed React Query fetches.
 *
 * Designed to drop into any DataCard widget in place of a SkeletonList.
 * Extracts the message from ApiError when available, otherwise falls
 * back to a generic description. Shows a retry button if onRetry is
 * provided (typically `refetch` from useQuery).
 */
export function QueryError({ error, onRetry, compact }: QueryErrorProps) {
  const message =
    error instanceof ApiError
      ? error.status >= 500
        ? 'The server encountered an error'
        : error.message
      : error instanceof Error
        ? error.message
        : 'An unexpected error occurred';

  return (
    <div className="flex flex-col items-center justify-center py-4 text-center animate-fade-in">
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--danger)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="mb-1.5 opacity-60"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <div className="text-xs font-semibold text-ink-muted">
        Failed to load
      </div>
      {!compact && (
        <div className="mt-0.5 max-w-xs text-2xs text-ink-dim leading-snug">
          {message}
        </div>
      )}
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-2 rounded-lg border border-border bg-surface-2 px-3 py-1 text-2xs font-medium text-ink-muted hover:bg-surface-1 hover:text-ink transition-colors"
        >
          Try again
        </button>
      )}
    </div>
  );
}
